import { useEffect, useRef, useState, useCallback } from 'react'

// Custom hook for managing focus and accessibility states
export const useAccessibility = () => {
  const [announcements, setAnnouncements] = useState<string[]>([])
  const announcementRef = useRef<HTMLDivElement>(null)

  // Announce messages to screen readers
  const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
    setAnnouncements(prev => [...prev, message])
    
    // Clear announcement after it's been read
    setTimeout(() => {
      setAnnouncements(prev => prev.filter(msg => msg !== message))
    }, 1000)
  }, [])

  // Focus trap for modals
  const createFocusTrap = useCallback((element: HTMLElement) => {
    const focusableElements = element.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements[0] as HTMLElement
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

    const handleTabKey = (e: KeyboardEvent) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstElement) {
            lastElement.focus()
            e.preventDefault()
          }
        } else {
          if (document.activeElement === lastElement) {
            firstElement.focus()
            e.preventDefault()
          }
        }
      }
    }

    element.addEventListener('keydown', handleTabKey)
    firstElement.focus()

    return () => {
      element.removeEventListener('keydown', handleTabKey)
    }
  }, [])

  // Keyboard navigation helper
  const handleKeyNavigation = useCallback((
    e: KeyboardEvent,
    onEnter?: () => void,
    onSpace?: () => void,
    onEscape?: () => void,
    onArrowUp?: () => void,
    onArrowDown?: () => void,
    onArrowLeft?: () => void,
    onArrowRight?: () => void
  ) => {
    switch (e.key) {
      case 'Enter':
        if (onEnter) {
          e.preventDefault()
          onEnter()
        }
        break
      case ' ':
        if (onSpace) {
          e.preventDefault()
          onSpace()
        }
        break
      case 'Escape':
        if (onEscape) {
          e.preventDefault()
          onEscape()
        }
        break
      case 'ArrowUp':
        if (onArrowUp) {
          e.preventDefault()
          onArrowUp()
        }
        break
      case 'ArrowDown':
        if (onArrowDown) {
          e.preventDefault()
          onArrowDown()
        }
        break
      case 'ArrowLeft':
        if (onArrowLeft) {
          e.preventDefault()
          onArrowLeft()
        }
        break
      case 'ArrowRight':
        if (onArrowRight) {
          e.preventDefault()
          onArrowRight()
        }
        break
    }
  }, [])

  // Check for reduced motion preference
  const prefersReducedMotion = useCallback(() => {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }, [])

  // High contrast mode detection
  const prefersHighContrast = useCallback(() => {
    return window.matchMedia('(prefers-contrast: high)').matches
  }, [])

  return {
    announce,
    createFocusTrap,
    handleKeyNavigation,
    prefersReducedMotion,
    prefersHighContrast,
    announcements
  }
}

// Hook for managing skip links
export const useSkipLinks = () => {
  const [skipLinks, setSkipLinks] = useState<Array<{ id: string; label: string }>>([])

  const addSkipLink = (id: string, label: string) => {
    setSkipLinks(prev => [...prev.filter(link => link.id !== id), { id, label }])
  }

  const removeSkipLink = (id: string) => {
    setSkipLinks(prev => prev.filter(link => link.id !== id))
  }

  const skipToContent = (id: string) => {
    const element = document.getElementById(id)
    if (element) {
      element.focus()
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  return {
    skipLinks,
    addSkipLink,
    removeSkipLink,
    skipToContent
  }
}

// Hook for managing aria-live regions
export const useLiveRegion = () => {
  const liveRegionRef = useRef<HTMLDivElement>(null)
  const [messages, setMessages] = useState<Array<{ id: string; message: string; level: 'polite' | 'assertive' }>>([])

  const announce = (message: string, level: 'polite' | 'assertive' = 'polite') => {
    const id = `msg-${Date.now()}-${Math.random()}`
    setMessages(prev => [...prev, { id, message, level }])
    
    // Remove message after it's been announced
    setTimeout(() => {
      setMessages(prev => prev.filter(msg => msg.id !== id))
    }, 2000)
  }

  const clear = () => {
    setMessages([])
  }

  return {
    liveRegionRef,
    announce,
    clear,
    messages
  }
}

// Hook for color contrast checking
export const useColorContrast = () => {
  const checkContrast = (foreground: string, background: string): number => {
    // Convert hex to RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : { r: 0, g: 0, b: 0 }
    }

    // Calculate relative luminance
    const getLuminance = (rgb: { r: number; g: number; b: number }) => {
      const { r, g, b } = rgb
      const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
      })
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs
    }

    const fg = hexToRgb(foreground)
    const bg = hexToRgb(background)
    
    const fgLuminance = getLuminance(fg)
    const bgLuminance = getLuminance(bg)
    
    const lighter = Math.max(fgLuminance, bgLuminance)
    const darker = Math.min(fgLuminance, bgLuminance)
    
    return (lighter + 0.05) / (darker + 0.05)
  }

  const meetsWCAG = (ratio: number, level: 'AA' | 'AAA', size: 'normal' | 'large' = 'normal') => {
    if (level === 'AA') {
      return size === 'large' ? ratio >= 3 : ratio >= 4.5
    } else {
      return size === 'large' ? ratio >= 4.5 : ratio >= 7
    }
  }

  return { checkContrast, meetsWCAG }
}

// Hook for managing focus indicators
export const useFocusManagement = () => {
  const [focusVisible, setFocusVisible] = useState(false)
  const lastInteractionWasKeyboard = useRef(false)

  useEffect(() => {
    const handleKeyDown = () => {
      lastInteractionWasKeyboard.current = true
      setFocusVisible(true)
    }

    const handleMouseDown = () => {
      lastInteractionWasKeyboard.current = false
      setFocusVisible(false)
    }

    const handleFocus = () => {
      if (lastInteractionWasKeyboard.current) {
        setFocusVisible(true)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('mousedown', handleMouseDown)
    document.addEventListener('focus', handleFocus, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('mousedown', handleMouseDown)
      document.removeEventListener('focus', handleFocus, true)
    }
  }, [])

  return { focusVisible }
}